import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';

export interface ActionTaken {
  type: string;
  incidentId?: string;
  deviceId?: string;
  dedupeKey?: string;
}

export interface TaskExecution {
  eventId: string;
  timestamp: string;
  taskId: string;
  taskType: string;
  taskName: string;
  durationMs: number;
  result: string;
  targetsEvaluated: number;
  targetsAlerting: number;
  targetsCleared: number;
  targetsUnchanged: number;
  actionsTaken: ActionTaken[];
}

interface TaskExecutionListResponse {
  executions: TaskExecution[];
  hasMore: boolean;
}

const backendApi = config.backendApi;

const fetchTaskExecutions = async (
  taskId: string,
  limit: number,
  offset: number
): Promise<TaskExecutionListResponse> => {
  const params = new URLSearchParams();
  params.append('limit', String(limit));
  params.append('offset', String(offset));

  const response = await axios.get<TaskExecutionListResponse>(
    `${backendApi}/api/alert-definitions/${taskId}/executions?${params.toString()}`,
    { withCredentials: true }
  );
  return response.data;
};

export function useTaskExecutions(
  taskId: string | undefined,
  options?: { limit?: number; offset?: number }
) {
  const contextReady = useActiveContextReady();
  const limit = options?.limit ?? 20;
  const offset = options?.offset ?? 0;

  return useQuery<TaskExecutionListResponse>({
    queryKey: ['task-executions', taskId, limit, offset],
    queryFn: () => {
      if (!taskId) throw new Error('taskId required');
      return fetchTaskExecutions(taskId, limit, offset);
    },
    enabled: !!taskId && contextReady,
  });
}
